import React, { useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { getJobs } from '../api/job';
import { Job } from '../types/job';
import { fetchCategories } from '../api/contractorProfile';
import { Category } from '../types/contractor';

export async function jobListLoader() {
  const [jobs, categories] = await Promise.all([getJobs(), fetchCategories()]);
  return { jobs, categories };
}

function formatBudget(min: number, max: number): string | null {
  if (min <= 0 && max <= 0) return null;
  if (min > 0 && max > 0) return `R${min.toLocaleString()} – R${max.toLocaleString()}`;
  if (min > 0) return `From R${min.toLocaleString()}`;
  return `Up to R${max.toLocaleString()}`;
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return new Date(timestamp).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}

const JobListPage: React.FC = () => {
  const { jobs: initialJobs, categories } = useLoaderData() as { jobs: Job[]; categories: Category[] };

  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const [categoryId, setCategoryId] = useState<string>('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFilter = async () => {
    setLoading(true);
    try {
      const filtered = await getJobs({
        categoryId: categoryId ? parseInt(categoryId) : undefined,
        location: location || undefined,
      });
      setJobs(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setCategoryId('');
    setLocation('');
    setLoading(true);
    try {
      setJobs(await getJobs());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background text-on-background min-h-screen pb-24 md:pb-0">
      <TopAppBar />

      <main className="max-w-4xl mx-auto px-4 pt-8 pb-12">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-headline-md text-on-surface">Open Jobs</h1>
          {jobs.length > 0 && (
            <span className="text-sm text-on-surface-variant">{jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}</span>
          )}
        </div>

        {/* Filter bar */}
        <div className="bg-surface rounded-2xl border border-outline-variant/30 shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-outline text-[18px]">category</span>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-outline-variant bg-surface-container-lowest text-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-outline text-[18px]">location_on</span>
              <input
                type="text"
                placeholder="City or suburb"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-outline-variant bg-surface-container-lowest text-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-outline"
              />
            </div>
            <button
              onClick={handleFilter}
              className="bg-primary text-on-primary px-5 py-2.5 rounded-xl font-label-bold hover:bg-primary/90 transition-colors text-sm"
            >
              Search
            </button>
            {(categoryId || location) && (
              <button
                onClick={handleClear}
                className="text-secondary font-label-bold px-4 py-2.5 rounded-xl border border-outline-variant hover:bg-surface-container transition-colors text-sm flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined text-[16px]">close</span>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-on-surface-variant">
            <span className="material-symbols-outlined text-[24px] animate-spin">progress_activity</span>
            Searching...
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined text-[48px] text-outline-variant mb-3 block">search_off</span>
            <p className="font-label-bold">No open jobs found.</p>
            {(categoryId || location) && (
              <button onClick={handleClear} className="mt-3 text-secondary text-sm font-label-bold hover:underline">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const budget = formatBudget(job.budgetMin, job.budgetMax);
              return (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="group flex gap-4 bg-surface rounded-2xl border border-outline-variant/30 shadow-sm hover:shadow-md transition-all p-4"
                >
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-surface-container-low">
                    {job.pictureUrl ? (
                      <img src={job.pictureUrl} alt={job.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-outline-variant text-[28px]">handyman</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h2 className="font-label-bold text-on-surface group-hover:text-primary transition-colors line-clamp-1">{job.title}</h2>
                      <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-label-bold bg-green-100 text-green-800">
                        Open
                      </span>
                    </div>
                    <p className="text-on-surface-variant text-sm line-clamp-2 mb-2">{job.description}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-on-surface-variant">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">location_on</span>
                        {job.location}
                      </span>
                      {job.categoryName && (
                        <span className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[13px]">category</span>
                          {job.categoryName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-[13px]">schedule</span>
                        {timeAgo(job.createdAt)}
                      </span>
                      {budget && (
                        <span className="flex items-center gap-1 font-label-bold text-primary">
                          <span className="material-symbols-outlined text-[13px]">payments</span>
                          {budget}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 flex items-center self-center">
                    <span className="material-symbols-outlined text-outline-variant group-hover:text-primary transition-colors text-[20px]">chevron_right</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <BottomNavBar />
    </div>
  );
};

export default JobListPage;
